<template>
  <div v-if="modelValue" class="modal-overlay" @click.self="close">
    <div class="modal-card">
      <div class="section-header">
        <h3>{{ mode === 'edit' ? 'Edit Job' : 'New Job' }}</h3>
        <button class="icon-button" type="button" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <p v-if="error" class="banner banner-error">{{ error }}</p>

        <label class="modal-field">
          <span>Title *</span>
          <input v-model="draft.title" type="text" placeholder="e.g. Panel upgrade — 123 Main St" />
        </label>
        <label class="modal-field">
          <span>Starts *</span>
          <input v-model="draft.scheduled_start" type="datetime-local" />
        </label>
        <label class="modal-field">
          <span>Ends</span>
          <input v-model="draft.scheduled_end" type="datetime-local" />
        </label>
        <label v-if="mode === 'create' && !prefill?.contact_id" class="modal-field">
          <span>Contact</span>
          <select v-model="draft.contact_id">
            <option :value="''">No contact</option>
            <option v-for="contact in contacts" :key="contact.id" :value="contact.id">
              {{ contact.contact_name }}
            </option>
          </select>
        </label>
        <label v-if="mode === 'edit'" class="modal-field">
          <span>Status</span>
          <select v-model="draft.status">
            <option v-for="status in jobStatuses" :key="status" :value="status">
              {{ jobStatusLabels[status] }}
            </option>
          </select>
        </label>
        <label class="modal-field">
          <span>Address</span>
          <input v-model="draft.address" type="text" placeholder="Where the work happens" />
        </label>
        <label class="modal-field">
          <span>Description</span>
          <textarea v-model="draft.description" rows="3" placeholder="What's the job?"></textarea>
        </label>
        <label class="modal-field">
          <span>Notes</span>
          <textarea v-model="draft.notes" rows="2" placeholder="Optional internal notes"></textarea>
        </label>
      </div>

      <div class="modal-actions">
        <button class="icon-button" type="button" @click="close">Cancel</button>
        <button
          class="send-button"
          type="button"
          :disabled="!draft.title.trim() || !draft.scheduled_start || sending"
          @click="save"
        >
          {{ sending ? (mode === 'edit' ? 'Saving…' : 'Scheduling…') : (mode === 'edit' ? 'Save changes' : 'Schedule job') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import { jobStatuses, jobStatusLabels } from '@/constants/jobs'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  mode: { type: String, required: true }, // 'create' | 'edit'
  job: { type: Object, default: null },
  companyId: { type: String, default: '' },
  prefill: { type: Object, default: null }, // { quote_id?, contact_id?, lead_id?, title?, address? }
})

const emit = defineEmits(['update:modelValue', 'saved'])

function blankDraft() {
  return { title: '', scheduled_start: '', scheduled_end: '', contact_id: '', status: 'scheduled', address: '', description: '', notes: '' }
}

function toLocalInputValue(value) {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const draft = ref(blankDraft())
const contacts = ref([])
const sending = ref(false)
const error = ref('')

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return

    error.value = ''

    if (props.mode === 'edit' && props.job) {
      draft.value = {
        title: props.job.title || '',
        scheduled_start: toLocalInputValue(props.job.scheduled_start),
        scheduled_end: toLocalInputValue(props.job.scheduled_end),
        contact_id: props.job.contact_id || '',
        status: props.job.status || 'scheduled',
        address: props.job.address || '',
        description: props.job.description || '',
        notes: props.job.notes || '',
      }
    } else {
      draft.value = {
        ...blankDraft(),
        title: props.prefill?.title || '',
        contact_id: props.prefill?.contact_id || '',
        address: props.prefill?.address || '',
      }

      if (!props.prefill?.contact_id) loadContacts()
    }
  }
)

function close() {
  emit('update:modelValue', false)
}

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function loadContacts() {
  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/contacts?company_id=${encodeURIComponent(props.companyId)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))
    if (response.ok && data.ok) contacts.value = data.contacts || []
  } catch {
    contacts.value = []
  }
}

async function save() {
  const title = draft.value.title.trim()

  if (!title || !draft.value.scheduled_start || sending.value) return

  if (draft.value.scheduled_end && draft.value.scheduled_end < draft.value.scheduled_start) {
    error.value = 'The end time must be after the start time.'
    return
  }

  sending.value = true
  error.value = ''

  const isEdit = props.mode === 'edit'

  try {
    const headers = await authHeaders()
    const url = isEdit ? `${API_BASE_URL}/jobs/${props.job.id}` : `${API_BASE_URL}/jobs`
    const method = isEdit ? 'PATCH' : 'POST'
    const body = isEdit
      ? {
          title,
          scheduled_start: draft.value.scheduled_start,
          scheduled_end: draft.value.scheduled_end || null,
          status: draft.value.status,
          address: draft.value.address.trim(),
          description: draft.value.description.trim(),
          notes: draft.value.notes.trim(),
        }
      : {
          company_id: props.companyId,
          title,
          scheduled_start: draft.value.scheduled_start,
          scheduled_end: draft.value.scheduled_end || undefined,
          contact_id: draft.value.contact_id || undefined,
          lead_id: props.prefill?.lead_id || undefined,
          quote_id: props.prefill?.quote_id || undefined,
          address: draft.value.address.trim() || undefined,
          description: draft.value.description.trim() || undefined,
          notes: draft.value.notes.trim() || undefined,
        }

    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => ({}))

    if (response.status === 409 && data.existing_job_id) {
      error.value = data.error || 'This quote already has a scheduled job.'
      return
    }

    if (!response.ok || !data.ok) {
      throw new Error(data.error || `Failed to ${isEdit ? 'update' : 'schedule'} job.`)
    }

    const token = await getAuthToken()
    logAction(token, {
      action: isEdit ? 'job.update' : 'job.create',
      label: isEdit ? `Updated job ${data.job.title}` : `Scheduled job ${data.job.title}`,
      company_id: props.companyId,
      metadata: { job_id: data.job.id, quote_id: data.job.quote_id || undefined },
    })

    emit('saved', data.job)
    emit('update:modelValue', false)
  } catch (err) {
    error.value = err.message || 'Something went wrong.'
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.banner {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.icon-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-subtle);
}

.section-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 800;
}

.send-button {
  min-width: 130px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  align-self: end;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
}

.send-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(23, 35, 31, 0.45);
}

.modal-card {
  width: min(440px, 100%);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 24px 60px rgba(23, 35, 31, 0.25);
}

.modal-body {
  display: grid;
  gap: 12px;
  padding: 16px;
  overflow: auto;
}

.modal-field {
  display: grid;
  gap: 5px;
}

.modal-field span {
  color: var(--text-secondary);
  font-size: 0.76rem;
  font-weight: 750;
  text-transform: uppercase;
}

.modal-field input,
.modal-field select {
  height: 40px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.modal-field textarea {
  width: 100%;
  min-width: 0;
  resize: vertical;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 11px 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
  outline: none;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid var(--border-subtle);
}
</style>
